import { QuestelServicesClient, RequestResult } from '@questelservices/common';
import store from '@/store';
import { LoadOptions } from 'devextreme/data/load_options';
import { SelectOption } from '@/models/SelectOption';
import { AgentDto } from '@/models/agent/AgentDto';
import { ClientDto, ClientDetailDto } from '@/models/client/ClientDto';
import { ClientLookupDataDto } from '@/models/client/ClientLookupDataDto';
import { ClientReportOptionDto } from '@/models/client/ClientReportOptionDto';
import { CreateClientDto } from '@/models/client/CreateClientDto';
import { InvoiceLookupDataDto } from '@/models/client/InvoiceLookupDataDto';
import { MatterDto } from '@/models/matter/MatterDto';
import _ from 'lodash';

export class ClientService {
  private readonly client: QuestelServicesClient;

  constructor() {
    this.client = new QuestelServicesClient(store, '/api/clients');
  }

  async getAll(): Promise<ClientDto[]> {
    return await this.client.get('');
  }

  async getAllSelectOptions(includeAll: boolean = false): Promise<SelectOption[]> {
    let clients = await this.getAll();
    let result = clients.map<SelectOption>(c => {
      return { text: c.ClientName, value: c.ClientId };
    });

    if (includeAll) {
      result.unshift({ text: 'ALL', value: '' });
    }

    return result;
  }

  async getEbillers(): Promise<ClientDto[]> {
    return await this.client.get('/e-billers');
  }

  async getEbillerSelectOptions(): Promise<SelectOption[]> {
    let clients = await this.getEbillers();
    return clients.map<SelectOption>(c => {
      return {
        text: c.ClientName,
        value: c.ClientId
      };
    });
  }

  async getAgentByClientId(clientId: Guid): Promise<AgentDto[]> {
    return await this.client.get(`/${clientId}/agents`);
  }

  async getMatters(clientId: Guid): Promise<MatterDto[]> {
    return await this.client.get<MatterDto[]>(`/${clientId}/matters`);
  }

  async getMatterSelectOptions(clientId: Guid): Promise<SelectOption[]> {
    let matters = await this.getMatters(clientId);
    let matterNames = _.uniq(matters.map(m => m.MatterName));
    return matterNames.map<SelectOption>(m => {
      return { text: m, value: m };
    });
  }

  async getClientReportOption(clientId: Guid): Promise<ClientReportOptionDto> {
    return await this.client.get(`/${clientId}/report-option`);
  }

  async addClientReportOption(clientId: Guid, option:ClientReportOptionDto): Promise<any> {
    return await this.client.postJson(`/${clientId}/report-option`, option);
  }

  async updateClientReportOption(clientId: Guid, option:ClientReportOptionDto): Promise<any> {
    return await this.client.putJson(`/${clientId}/report-option`, option);
  }

  async getClientDetails(clientId: Guid): Promise<ClientDetailDto> {
    return await this.client.get(`/${clientId}/details`);
  }

  async updateClientDetails(clientId: Guid, clientDto: ClientDetailDto): Promise<RequestResult<ClientDetailDto>> {
    return await this.client.putJson(`/${clientId}/update`, clientDto);
  }

  async createClient(createClientDto: CreateClientDto): Promise<RequestResult> {
    return await this.client.postJson("/create", createClientDto);
  }

  async getClientLineItems(clientId: Guid, options: LoadOptions): Promise<any> {
    return await this.client.getPostData(`/${clientId}/line-items`, options);
  }

  async getLookupData(clientId: Guid, batchId: Guid | null, agentId: Guid | null, invoiceNumber: string | null, countryId: number | null): Promise<ClientLookupDataDto> {
    let url = `/${clientId}/lookup-data`;
    let parameters: string[] = [];
    let query: string = '';

    if (batchId != null) {
      parameters.push(`batchId=${encodeURIComponent(batchId)}`);
    }

    if (agentId != null) {
      parameters.push(`agentId=${encodeURIComponent(agentId)}`);
    }

    if (invoiceNumber != null) {
      parameters.push(`invoiceNumber=${encodeURIComponent(invoiceNumber)}`);
    }

    if (countryId != null) {
      parameters.push(`countryId=${encodeURIComponent(countryId)}`);
    }

    if (parameters.length > 0) {
      query = `?${parameters.join('&')}`;
    }

    return await this.client.get<ClientLookupDataDto>(`${url}${query}`);
  }

  async getInvoiceLookupData(clientId: Guid, batchId: Guid | null, agentId: Guid | null, countryId: number | null): Promise<InvoiceLookupDataDto> {
    let url = `/${clientId}/invoice-lookup-data`;
    let parameters: string[] = [];
    let query: string = '';

    if (batchId != null) {
      parameters.push(`batchId=${encodeURIComponent(batchId)}`);
    }

    if (agentId != null) {
      parameters.push(`agentId=${encodeURIComponent(agentId)}`);
    }

    if (countryId != null) {
      parameters.push(`countryId=${encodeURIComponent(countryId)}`);
    }

    if (parameters.length > 0) {
      query = `?${parameters.join('&')}`;
    }

    return await this.client.get<InvoiceLookupDataDto>(`${url}${query}`);
  }
}
